/**
 * Optimiseur agressif d'images pour réduire drastiquement la taille des fichiers
 * Cible: économie de 28,815 KiB identifiée par Lighthouse
 */

interface ImageOptimizationConfig {
  maxWidth: number;
  maxHeight: number;
  quality: number;
  format: 'webp' | 'avif' | 'jpeg';
}

// Configurations optimales par type d'image
export const IMAGE_CONFIGS: Record<string, ImageOptimizationConfig> = {
  logo: {
    maxWidth: 120,
    maxHeight: 120,
    quality: 55, // Ultra-compressé
    format: 'webp'
  },
  banner: {
    maxWidth: 1200,
    maxHeight: 400,
    quality: 50, // Ultra-compressé
    format: 'webp'
  },
  avatar: {
    maxWidth: 80,
    maxHeight: 80,
    quality: 55, // Ultra-compressé
    format: 'webp'
  },
  backdrop: {
    maxWidth: 1920,
    maxHeight: 800,
    quality: 45, // Ultra-compressé
    format: 'webp'
  },
  category: {
    maxWidth: 800,
    maxHeight: 450,
    quality: 50, // Ultra-compressé
    format: 'webp'
  }
};

/**
 * Optimise une URL d'image Supabase avec compression agressive
 */
export function optimizeImageUrl(
  url: string, 
  type: keyof typeof IMAGE_CONFIGS = 'logo',
  forceFormat?: 'webp' | 'avif'
): string {
  if (!url || !url.includes('supabase.co/storage')) {
    return url;
  }
  
  // Ne pas ré-optimiser les images déjà optimisées
  if (url.includes('?width=') || url.includes('&width=')) {
    return url;
  }
  
  const config = IMAGE_CONFIGS[type];
  const format = forceFormat || config.format;
  
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.set('width', config.maxWidth.toString());
    urlObj.searchParams.set('height', config.maxHeight.toString());
    urlObj.searchParams.set('quality', config.quality.toString());
    urlObj.searchParams.set('format', format);
    urlObj.searchParams.set('resize', 'contain');
    
    return urlObj.toString();
  } catch (e) {
    console.error('[Image Optimizer] Failed to optimize URL:', e);
    return url;
  }
}

/**
 * Génère un srcset responsive optimisé
 */
export function generateOptimizedSrcSet(
  baseUrl: string,
  type: keyof typeof IMAGE_CONFIGS = 'logo'
): string {
  if (!baseUrl || !baseUrl.includes('supabase.co/storage')) {
    return '';
  }
  
  const config = IMAGE_CONFIGS[type];
  
  // Tailles responsives basées sur le type
  const sizes = type === 'logo' ? [60, 120, 180] :
                type === 'avatar' ? [40, 80, 120] :
                type === 'banner' ? [640, 960, 1200] :
                type === 'backdrop' ? [960, 1280, 1920] :
                [400, 600, 800];
  
  return sizes
    .map(width => {
      const height = Math.round(width * (config.maxHeight / config.maxWidth));
      const url = optimizeImageUrl(baseUrl, type);
      
      try {
        const urlObj = new URL(url);
        urlObj.searchParams.set('width', width.toString());
        urlObj.searchParams.set('height', height.toString());
        return `${urlObj.toString()} ${width}w`;
      } catch (e) {
        return '';
      }
    })
    .filter(Boolean)
    .join(', ');
}

/**
 * Génère l'attribut sizes pour responsive images
 */
export function generateSizesAttribute(type: keyof typeof IMAGE_CONFIGS = 'logo'): string {
  const sizesMap: Record<keyof typeof IMAGE_CONFIGS, string> = {
    logo: '(max-width: 640px) 60px, (max-width: 1024px) 120px, 180px',
    avatar: '(max-width: 640px) 40px, (max-width: 1024px) 80px, 120px',
    banner: '(max-width: 640px) 640px, (max-width: 1024px) 960px, 1200px',
    backdrop: '100vw',
    category: '(max-width: 640px) 400px, (max-width: 1024px) 600px, 800px'
  };
  
  return sizesMap[type];
}

/**
 * Précharge une image critique avec optimisation
 */
export function preloadCriticalImage(url: string, type: keyof typeof IMAGE_CONFIGS = 'logo') {
  if (typeof window === 'undefined') return;
  
  // Ne pas précharger sur connexions lentes
  if ('connection' in navigator) {
    const conn = (navigator as any).connection;
    if (conn?.saveData || conn?.effectiveType === 'slow-2g' || conn?.effectiveType === '2g') {
      return;
    }
  }
  
  const optimizedUrl = optimizeImageUrl(url, type);
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = optimizedUrl;
  link.fetchPriority = 'high';
  link.type = 'image/webp';
  
  document.head.appendChild(link);
}

/**
 * Détecte si le navigateur supporte AVIF
 */
let avifSupport: boolean | null = null;

export async function supportsAVIF(): Promise<boolean> {
  if (avifSupport !== null) return avifSupport;
  
  return new Promise((resolve) => {
    const avif = new Image();
    avif.onload = () => {
      avifSupport = true;
      resolve(true);
    };
    avif.onerror = () => {
      avifSupport = false;
      resolve(false);
    };
    avif.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABcAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB9tZGF0EgAKCBgABogQEDQgMgkQAAAAB8dSLfI=';
  });
}

/**
 * Optimise toutes les images de la page de manière agressive
 */
export function optimizeAllPageImages() {
  if (typeof window === 'undefined') return;
  
  // Trouver toutes les images Supabase non optimisées
  const images = Array.from(document.querySelectorAll('img[src*="supabase.co/storage"]'));
  
  images.forEach((img: Element) => {
    const imgElement = img as HTMLImageElement;
    const src = imgElement.src;
    
    // Ne pas ré-optimiser les images déjà optimisées
    if (src.includes('?width=') || src.includes('&width=')) {
      return;
    }
    
    // Détecter le type d'image basé sur les classes
    let type: keyof typeof IMAGE_CONFIGS = 'logo';
    if (imgElement.className.includes('banner')) type = 'banner';
    else if (imgElement.className.includes('avatar')) type = 'avatar';
    else if (imgElement.className.includes('backdrop')) type = 'backdrop';
    else if (imgElement.className.includes('category')) type = 'category';
    
    // Optimiser l'URL
    const optimizedUrl = optimizeImageUrl(src, type);
    
    // Appliquer l'optimisation
    imgElement.src = optimizedUrl;
    
    // Ajouter srcset si pas déjà présent
    if (!imgElement.srcset) {
      const srcset = generateOptimizedSrcSet(src, type);
      if (srcset) {
        imgElement.srcset = srcset;
        imgElement.sizes = generateSizesAttribute(type);
      }
    }
    
    // Ajouter loading lazy sauf pour les images critiques
    if (!imgElement.getAttribute('loading') && !imgElement.getAttribute('fetchpriority')) {
      imgElement.loading = 'lazy';
    }
    
    // Ajouter decoding async
    if (!imgElement.getAttribute('decoding')) {
      imgElement.decoding = 'async';
    }
  });
}

/**
 * Active l'optimisation automatique au chargement de la page
 */
export function enableAutoImageOptimization() {
  if (typeof window === 'undefined') return;
  
  // Optimiser au chargement initial
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', optimizeAllPageImages);
  } else {
    optimizeAllPageImages();
  }
  
  // Observer pour les nouvelles images ajoutées dynamiquement
  const observer = new MutationObserver((mutations) => {
    let hasNewImages = false;
    
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          if (element.tagName === 'IMG' || element.querySelector('img')) {
            hasNewImages = true;
          }
        }
      });
    });
    
    if (hasNewImages) {
      // Débounce pour éviter les optimisations trop fréquentes
      setTimeout(optimizeAllPageImages, 100);
    }
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  return () => observer.disconnect();
}
